import type { $Fetch } from 'ofetch';
import type { CourseGateway } from '@/application/port/out/CourseGateway';
import type {
  CreateCourseCommand,
  ListCoursesQuery,
  UpdateCourseCommand,
} from '@/application/port/in/dto/CourseCommand';
import type { Course } from '@/domain/model/Course';
import type { Discipline } from '@/domain/model/Discipline';
import type { Page } from '@/domain/model/Page';
import type { ApiResponse, SpringPage } from '@/infrastructure/http/types';
import { assertApiSuccess } from '@/infrastructure/http/assertApiSuccess';

const COURSES_PATH = '/api/v1/courses';

export class HttpCourseGateway implements CourseGateway {
  constructor(private readonly client: $Fetch) {}

  async list(query: ListCoursesQuery): Promise<Page<Course>> {
    const params: Record<string, string | number> = {};
    if (query.name) params['name'] = query.name;
    if (typeof query.page === 'number') params['page'] = query.page;
    if (typeof query.size === 'number') params['size'] = query.size;
    if (query.sort) params['sort'] = query.sort;

    const res = await this.client<ApiResponse<SpringPage<Course>>>(COURSES_PATH, {
      method: 'GET',
      params,
    });
    assertApiSuccess(res);
    return this.toDomainPage(res.data);
  }

  async getById(id: string): Promise<Course> {
    const res = await this.client<ApiResponse<Course>>(
      `${COURSES_PATH}/${encodeURIComponent(id)}`,
      { method: 'GET' },
    );
    assertApiSuccess(res);
    return res.data;
  }

  async create(command: CreateCourseCommand): Promise<Course> {
    const res = await this.client<ApiResponse<Course>>(COURSES_PATH, {
      method: 'POST',
      body: command,
    });
    assertApiSuccess(res);
    return res.data;
  }

  async update(id: string, command: UpdateCourseCommand): Promise<Course> {
    const res = await this.client<ApiResponse<Course>>(
      `${COURSES_PATH}/${encodeURIComponent(id)}`,
      { method: 'PUT', body: command },
    );
    assertApiSuccess(res);
    return res.data;
  }

  async publish(id: string): Promise<Course> {
    const res = await this.client<ApiResponse<Course>>(
      `${COURSES_PATH}/${encodeURIComponent(id)}/publish`,
      { method: 'PATCH' },
    );
    assertApiSuccess(res);
    return res.data;
  }

  async archive(id: string): Promise<void> {
    await this.client<void>(
      `${COURSES_PATH}/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
    );
  }

  async listDisciplines(courseId: string): Promise<Discipline[]> {
    const res = await this.client<ApiResponse<Discipline[]>>(
      `${COURSES_PATH}/${encodeURIComponent(courseId)}/disciplines`, { method: 'GET' });
    assertApiSuccess(res);
    return res.data;
  }

  async chooseCurriculum(courseId: string, curriculumId: string): Promise<Course> {
    const res = await this.client<ApiResponse<Course>>(`${COURSES_PATH}/${encodeURIComponent(courseId)}/curriculum`, { method: 'PUT', body: { curriculumId } });
    assertApiSuccess(res);
    return res.data;
  }

  private toDomainPage(p: SpringPage<Course>): Page<Course> {
    return {
      content: p.content,
      number: p.number,
      size: p.size,
      totalElements: p.totalElements,
      totalPages: p.totalPages,
      first: p.first,
      last: p.last,
    };
  }
}
